import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { useTranslation } from "react-i18next";
import { SiteShell } from "@/components/site/SiteShell";
import { AuthBackdrop } from "@/components/site/AuthBackdrop";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/site/Logo";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { verifyRecaptcha } from "@/lib/recaptcha.functions";

export const Route = createFileRoute("/signup")({
  head: () => ({
    meta: [
      { title: "Sign up — Online Tax VAT Bangladesh" },
      { name: "description", content: "Create your free Online Tax VAT Bangladesh account in minutes. File income tax returns, manage VAT, and book trusted compliance services from anywhere in Bangladesh." },
      { name: "robots", content: "noindex, follow" },
    ],
    links: [{ rel: "canonical", href: "https://onlinetaxvat.com/signup" }],
  }),
  validateSearch: (search: Record<string, unknown>) => {
    const ref = typeof search.ref === "string" ? search.ref : undefined;
    return ref ? { ref } : ({} as { ref?: string });
  },
  component: SignupPage,
});

const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY as string | undefined;

declare global {
  interface Window {
    grecaptcha?: {
      ready: (cb: () => void) => void;
      execute: (siteKey: string, opts: { action: string }) => Promise<string>;
    };
  }
}

function SignupPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [referralCode, setReferralCode] = useState((search.ref ?? "").toUpperCase());
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (!RECAPTCHA_SITE_KEY) return;
    if (typeof window === "undefined") return;
    if (document.querySelector('script[data-recaptcha="v3"]')) return;
    // Defer reCAPTCHA (~250KB) until the browser is idle so it never
    // competes with signup-form LCP / hydration on mobile.
    const load = () => {
      if (document.querySelector('script[data-recaptcha="v3"]')) return;
      const s = document.createElement("script");
      s.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
      s.async = true;
      s.defer = true;
      s.dataset.recaptcha = "v3";
      document.head.appendChild(s);
    };
    const w = window as any;
    const h = typeof w.requestIdleCallback === "function"
      ? w.requestIdleCallback(load, { timeout: 3000 })
      : window.setTimeout(load, 1500);
    return () => {
      if (typeof w.cancelIdleCallback === "function") w.cancelIdleCallback(h);
      else clearTimeout(h);
    };
  }, []);

  async function getCaptchaToken(): Promise<string | null> {
    if (!RECAPTCHA_SITE_KEY || !window.grecaptcha) return null;
    try {
      return await new Promise<string>((resolve, reject) => {
        window.grecaptcha!.ready(async () => {
          try {
            const token = await window.grecaptcha!.execute(RECAPTCHA_SITE_KEY, { action: "signup" });
            resolve(token);
          } catch (e) { reject(e); }
        });
      });
    } catch { return null; }
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);

    const token = await getCaptchaToken();
    if (token) {
      try {
        const res = await verifyRecaptcha({ data: { token, action: "signup" } });
        if (!res.ok) {
          setLoading(false);
          toast.error(t("auth.signup.captchaFailed"));
          return;
        }
      } catch {
        // fail-open
      }
    }

    const redirectUrl = `${window.location.origin}/`;
    const cleanedRef = referralCode.trim().toUpperCase();
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName,
          phone,
          ...(cleanedRef ? { referred_by: cleanedRef } : {}),
        },
      },
    });
    setLoading(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(t("auth.signup.successToast"));
    navigate({ to: "/" });
  }

  return (
    <SiteShell>
      <AuthBackdrop />
      <section className="pt-14 md:pt-20 pb-20 relative">
        <div className="container-page max-w-md relative">
          {/* Reserve card height so late-arriving reCAPTCHA note / Logo query
              never shifts the form below the fold (CLS guard). */}
          <div className="glass-strong rounded-3xl p-8 shadow-glow" style={{ minHeight: 720 }}>
            <div className="flex justify-center"><Logo size="lg" /></div>
            <h1 className="mt-6 font-display font-bold text-2xl text-center">{t("auth.signup.title")}</h1>
            <p className="text-sm text-muted-foreground text-center mt-1">{t("auth.signup.subtitle")}</p>
            <form onSubmit={onSubmit} className="mt-6 space-y-4">
              <div>
                <Label htmlFor="name">{t("auth.signup.fullName")}</Label>
                <Input id="name" required maxLength={100} value={fullName} onChange={(e) => setFullName(e.target.value)} className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="phone">{t("auth.signup.phone")}</Label>
                <Input id="phone" required maxLength={20} placeholder="01XXXXXXXXX" value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="email">{t("auth.signup.email")}</Label>
                <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="password">{t("auth.signup.password")}</Label>
                <div className="relative mt-1.5">
                  <Input id="password" type={showPassword ? "text" : "password"} required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} className="pr-10" />
                  <button type="button" onClick={() => setShowPassword((s) => !s)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground">
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div>
                <Label htmlFor="ref">Referral Code <span className="text-muted-foreground font-normal">(optional)</span></Label>
                <Input
                  id="ref"
                  maxLength={32}
                  placeholder="e.g. TXR12"
                  value={referralCode}
                  onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                  className="mt-1.5 font-mono uppercase"
                />
              </div>
              <Button type="submit" disabled={loading} className="w-full gradient-primary text-primary-foreground shadow-glow">
                {loading ? t("auth.signup.submitting") : t("auth.signup.submit")}
              </Button>
            </form>
            {RECAPTCHA_SITE_KEY && (
              <p className="mt-4 text-[11px] text-muted-foreground text-center leading-relaxed">
                {t("auth.signup.captchaNote")}{" "}
                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="underline">{t("auth.signup.privacy")}</a>{" "}&{" "}
                <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="underline">{t("auth.signup.terms")}</a>.
              </p>
            )}
            <p className="mt-6 text-sm text-center text-muted-foreground">
              {t("auth.signup.haveAccount")} <Link to="/login" className="text-primary font-semibold hover:underline">{t("auth.signup.loginLink")}</Link>
            </p>
          </div>
        </div>
      </section>
    </SiteShell>
  );
}
